const PLANNR_APP_URL = "https://app.plannrcrm.com";

/** Direct link to a household contact profile in Plannr. */
export function plannrContactProfileUrl(accountUuid: string): string {
  return `${PLANNR_APP_URL}/contacts/${encodeURIComponent(accountUuid)}`;
}

type ExternalId = { crm?: string; id?: unknown };

type PlannrCrmRefs = {
  plannr?: {
    primary_account_uuid?: unknown;
    member_account_uuids?: unknown;
    account_uuid?: unknown;
    uuid?: unknown;
  };
} | null | undefined;

type PlannrClientShape = {
  plannr_account_uuid?: string | null;
  crm_refs?: PlannrCrmRefs;
  members?: { role?: string; external_ids?: ExternalId[] }[];
  external_ids?: ExternalId[];
};

function plannrIdFromExternalIds(ids: ExternalId[] | null | undefined): string | null {
  for (const ext of ids ?? []) {
    if (ext?.crm === "plannr" && typeof ext.id === "string" && ext.id.trim()) {
      return ext.id.trim();
    }
  }
  return null;
}

function plannrIdFromCrmRefs(crmRefs: PlannrCrmRefs): string | null {
  const plannr = crmRefs?.plannr;
  if (!plannr || typeof plannr !== "object") return null;
  const primary = plannr.primary_account_uuid;
  if (typeof primary === "string" && primary.trim()) return primary.trim();
  if (Array.isArray(plannr.member_account_uuids)) {
    for (const uuid of plannr.member_account_uuids) {
      if (typeof uuid === "string" && uuid.trim()) return uuid.trim();
    }
  }
  for (const key of ["account_uuid", "uuid"] as const) {
    const value = plannr[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function plannrIdFromMembers(members: PlannrClientShape["members"]): string | null {
  if (!members?.length) return null;
  const primary = members.find((m) => m.role === "primary");
  const fromPrimary = plannrIdFromExternalIds(primary?.external_ids);
  if (fromPrimary) return fromPrimary;
  for (const member of members) {
    const id = plannrIdFromExternalIds(member.external_ids);
    if (id) return id;
  }
  return null;
}

/** Plannr household account UUID — mirrors API ``plannr_account_uuid`` resolution. */
export function plannrPrimaryAccountUuid(client: PlannrClientShape | null | undefined): string | null {
  if (!client) return null;
  const fromApi = client.plannr_account_uuid;
  if (typeof fromApi === "string" && fromApi.trim()) return fromApi.trim();
  return (
    plannrIdFromCrmRefs(client.crm_refs) ??
    plannrIdFromExternalIds(client.external_ids) ??
    plannrIdFromMembers(client.members)
  );
}

/** Profile URL for a client record that has a Plannr link. */
export function plannrProfileUrlForClient(client: PlannrClientShape | null | undefined): string | null {
  const uuid = plannrPrimaryAccountUuid(client);
  return uuid ? plannrContactProfileUrl(uuid) : null;
}
